import React from "react";
import styled from "styled-components";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { TimeseriesDayPoint } from "@/api/campaign";
import { LoadingContainer, LoadingText, Spinner } from "@/shared/components/Loading/Loading";
import { dropLeadingZeroDays, formatMetricTick, metricScaleMinimum } from "../../utils/timeseriesChart";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const ChartCard = styled.div`
	background: #ffffff;
	border-radius: 12px;
	padding: 24px;
	width: 100%;
`;

const ChartHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
`;

const ChartTitle = styled.h3`
	font-family: "Poppins";
	font-weight: 700;
	font-size: 18px;
	color: #222222;
	margin: 0;
`;

const InfoNote = styled.p`
	font-family: "Poppins";
	font-size: 12px;
	color: #969696;
	margin: 0 0 0 0;
`;

const EmptyChart = styled.div`
	height: 350px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: "Poppins";
	font-size: 14px;
	color: #969696;
`;

const ChartLoading = styled(LoadingContainer)`
	height: 350px;
`;

interface TimeseriesChartProps {
	series: TimeseriesDayPoint[];
	observationDays?: string[];
	timezone?: string;
	platform?: "all" | "tiktok" | "instagram" | "facebook" | "youtube";
	loading?: boolean;
}

const TimeseriesChart: React.FC<TimeseriesChartProps> = ({
	series,
	timezone = "UTC",
	platform = "all",
	loading = false,
}) => {
	const visible = dropLeadingZeroDays([...series].sort((left, right) => left.date.localeCompare(right.date)));
	const views = visible.map((point) => point.views);
	const engagements = visible.map((point) => point.engagements);
	const labels = visible.map((p) => {
		const d = new Date(`${p.date}T00:00:00Z`);
		return d.toLocaleDateString("en-AU", { day: "numeric", month: "short", timeZone: "UTC" });
	});

	const formatTicks = (value: unknown, _index: unknown, ticks: Array<{ value: number }>) =>
		formatMetricTick(
			Number(value),
			ticks.map((tick) => tick.value),
		);

	const data = {
		labels,
		datasets: [
			{
				label: "Views",
				data: views,
				borderColor: "#7B2FF7",
				backgroundColor: "#7B2FF7",
				tension: 0.4,
				borderWidth: 2,
				pointRadius: 0,
				showLine: true,
				spanGaps: false,
				yAxisID: "y",
			},
			{
				label: "Engagements",
				data: engagements,
				borderColor: "#F72586",
				backgroundColor: "#F72586",
				tension: 0.4,
				borderWidth: 2,
				pointRadius: 0,
				showLine: true,
				spanGaps: false,
				yAxisID: "y1",
			},
		],
	};

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		layout: { padding: { top: 0 } },
		interaction: { mode: "index" as const, intersect: false },
		plugins: {
			legend: {
				position: "top" as const,
				align: "end" as const,
				labels: {
					usePointStyle: true,
					pointStyle: "circle",
					font: { family: "Poppins", size: 12 },
					padding: 30,
					boxWidth: 8,
					boxHeight: 8,
				},
				title: { display: true, padding: 0 },
			},
		},
		scales: {
			y: {
				type: "linear" as const,
				position: "left" as const,
				beginAtZero: false,
				min: metricScaleMinimum(views),
				grace: "10%",
				ticks: {
					callback: formatTicks,
					font: { family: "Poppins", size: 11 },
					color: "#7B2FF7",
				},
				grid: { color: "#f0f0f0" },
			},
			y1: {
				type: "linear" as const,
				position: "right" as const,
				beginAtZero: false,
				min: metricScaleMinimum(engagements),
				grace: "10%",
				ticks: {
					callback: formatTicks,
					font: { family: "Poppins", size: 11 },
					color: "#F72586",
				},
				grid: { drawOnChartArea: false },
			},
			x: {
				ticks: { font: { family: "Poppins", size: 11 }, color: "#969696" },
				grid: { display: false },
			},
		},
	};

	return (
		<ChartCard>
			<ChartHeader>
				<ChartTitle>Views and Engagement Over Time</ChartTitle>
			</ChartHeader>
			<InfoNote>
				{platform === "all"
					? "All platform videos"
					: `${platform.charAt(0).toUpperCase() + platform.slice(1)} videos`}
				{" · "}
				Observed growth days only ({timezone})
			</InfoNote>
			{loading ? (
				<ChartLoading role="status" aria-live="polite">
					<Spinner />
					<LoadingText>Loading chart…</LoadingText>
				</ChartLoading>
			) : series.length > 0 ? (
				<div style={{ height: 350 }}>
					<Line data={data} options={options} />
				</div>
			) : (
				<EmptyChart>No observed metrics for this platform and period.</EmptyChart>
			)}
		</ChartCard>
	);
};

export default TimeseriesChart;
